import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import { connect } from 'react-redux';

import { useFetchCampaignSubmissionByID } from '../../../hooks';
import { VpIcon, VpDialogTitle, VpNoMediaCollected, VpDialogContent } from '../../components';
import { useMediaTableContext } from '../../../hooks/contexts';
import VpMediaInfo from './VpMediaInfo';
import VpUpdateThumbnailModal from '../../widgets/VpMediaTable/VpUpdateThumbnailModal';

import styles from './VpMediaSubmissionDetailsDialog.module.less';
import { getMediaChipMap } from '../../../utils';
import VpMediaListBox from '../../components/VpMediaListBox/VpMediaListBox';
import FullScreenDialog from '../FullScreenDialog/FullScreenDialog';
import VpCollectedInfoBox from '../../components/VpCollectedInfoBox/VpCollectedInfoBox';
import VpSubmissionDetailsResponses from './VpSubmissionDetailsResponses';
import { mapSubmissionResponses } from '../../../utils/mapSubmissionResponses';
import { getContentTaggingEnabled } from '../../../redux/modules/auth';

// styles
const svgStyles = {
  height: '12px',
  width: '12px'
};
const stackStyles = {
  width: '100%'
};
const contentStyles = {
  display: 'flex',
  overflowY: 'auto'
};

export const VpMediaSubmissionDetailsDialog = ({ open = false, onClose = null }) => {
  const {
    selectedSubmissionId,
    selectedMediaId,
    setSelectedMediaId,
    updatingThumbnail,
    setUpdatingThumbnail
  } = useMediaTableContext();
  const { data, isLoading } = useFetchCampaignSubmissionByID(selectedSubmissionId);

  const hasMedia = !!(data?.data?.videoContent?.length || data?.data?.attachmentsContent?.length);

  const attachmentContent = data?.data?.attachmentsContent || [];
  const videoContent = data?.data?.videoContent || [];
  const mediaList = [...videoContent, ...attachmentContent];

  const formattedDate = new Date(data?.data?.submissionDate)?.toDateString();

  const firstMedia = mediaList ? mediaList[0] : null;

  useEffect(() => {
    //if null selectedMediaId set it to first i.e no subrow click
    if (!selectedMediaId) {
      setSelectedMediaId(firstMedia?.mediaId || firstMedia?.attachmentId);
    }
  }, [firstMedia]);

  const responsesList = data?.data?.responses || [];
  const responses = mapSubmissionResponses(responsesList);

  const mediaLabels = useMemo(() => getMediaChipMap(responses), [responses]);

  const selectedMedia = mediaList?.find(
    (media) => media.mediaId === selectedMediaId || media.attachmentId === selectedMediaId
  );

  const centerContent = (
    <Box className={styles.vpHeaderDetailsStyle}>
      <VpIcon icon="Send" sx={svgStyles} />
      <Typography variant="span">{data?.data?.campaignName}</Typography>
      <Typography variant="span">●</Typography>
      <VpIcon icon="Calendar" sx={svgStyles} />
      <Typography variant="span">{formattedDate}</Typography>
    </Box>
  );

  const handleClick = (media) => {
    setSelectedMediaId(media?.mediaId || media?.attachmentId);
  };

  return (
    <FullScreenDialog fullWidth onClose={() => onClose(false)} open={open}>
      <VpDialogTitle
        onClose={() => onClose(false)}
        leftContent={`Responses ${data?.data.submitterName ? `/ ${data?.data.submitterName}` : ''}`}
        centerContent={centerContent}
      />
      <VpDialogContent sx={contentStyles} disablePadding>
        <Stack
          justifyContent="flex-start"
          divider={<Divider orientation="vertical" flexItem />}
          direction={{ xs: 'column-reverse', sm: 'row' }}
          sx={stackStyles}
        >
          <Box className={styles.vpDisplayDialogDetailsSection}>
            <Box className={styles.vpDetailsDisplayContextSections}>
              {isLoading ? (
                <Skeleton className={styles.vpMediaDetailsSkeletonList} variant="rectangular" />
              ) : hasMedia ? (
                <VpMediaListBox
                  mediaList={mediaList}
                  onClick={handleClick}
                  selectedMediaId={selectedMediaId}
                  mediaLabels={mediaLabels}
                />
              ) : (
                <VpNoMediaCollected />
              )}
            </Box>
            <Box className={styles.vpDetailsDisplayContextSections}>
              <VpCollectedInfoBox icon="Layout" value="Responses" amount={responses.length || 0} />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Prompts</TableCell>
                    <TableCell>Answers</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell>Prompts</TableCell>
                      <TableCell>Answers</TableCell>
                    </TableRow>
                  )}
                  <VpSubmissionDetailsResponses responses={responses} mediaLabels={mediaLabels} campaignId={data?.data?.campaignId}/>
                </TableBody>
              </Table>
            </Box>
          </Box>
          {hasMedia && <VpMediaInfo media={selectedMedia} />}
        </Stack>
      </VpDialogContent>
      <VpUpdateThumbnailModal
        selectedMedia={selectedMedia}
        updatingThumbnail={updatingThumbnail}
        setUpdatingThumbnail={setUpdatingThumbnail}
      />
    </FullScreenDialog>
  );
};

VpMediaSubmissionDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hasMedia: PropTypes.bool,
  contentTaggingEnabled: PropTypes.bool
};

const mapStateToProps = (state) => ({
  contentTaggingEnabled: getContentTaggingEnabled(state)
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(VpMediaSubmissionDetailsDialog);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaSubmissionDetailsDialog.jsx